/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CompsPayload } from '../models/CompsPayload';
import type { CompsResponse } from '../models/CompsResponse';
import type { ExpenseBenchmarksResponse } from '../models/ExpenseBenchmarksResponse';
import type { ForecastPayload } from '../models/ForecastPayload';
import type { ForecastResponse } from '../models/ForecastResponse';
import type { OwnerDataResponse } from '../models/OwnerDataResponse';
import type { PricingPayload } from '../models/PricingPayload';
import type { PricingResponse } from '../models/PricingResponse';
import type { PropertyDetailsResponse } from '../models/PropertyDetailsResponse';
import type { PropertyReportPropertyInsert } from '../models/PropertyReportPropertyInsert';
import type { PropertySearchResponse } from '../models/PropertySearchResponse';
import type { RecommendationResponse } from '../models/RecommendationResponse';
import type { RevenueManagementPayload } from '../models/RevenueManagementPayload';
import type { UUID } from '../models/UUID';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class PropertyService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * Search for a property by name, address, or other identifying text.
     *
     * This endpoint matches properties based on a query string. A well-formatted input—such as a full address—is more likely to return accurate results.
     * We also include known aliases and alternate addresses when available.
     *
     * **Important**: This search endpoint only returns properties from HelloData's indexed database. Private properties created via POST /property (custom-created properties) are not indexed and will not appear in search results. The intended workflow is to use this search endpoint to find HelloData property IDs initially, then store those IDs for future direct access. If you create a property via POST /property, store the returned ID and use it directly—do not expect to find it via search.
     *
     * **Recommendation**: Use filters whenever possible to improve performance and result quality.
     *
     * Filtering options:
     * - Use `lat` and `lon` together to sort results by proximity.
     * - Use `max_distance` (in miles) with `lat` and `lon` to limit results to a given radius.
     * - Use `state` (e.g. 'CA') or `zip_code` (5-digit or ZIP+4 formats) to restrict results by region.
     * @param q The search query string. Can include a property name, address, id, or partial text.
     * @param lat Latitude for proximity-based sorting. Must be used with `lon`.
     * @param lon Longitude for proximity-based sorting. Must be used with `lat`.
     * @param state Optional. State abbreviation to filter results (e.g. 'NY').
     * @param zipCode Optional. Zip code to filter results. Accepts 5-digit or ZIP+4 formats; missing leading zeros are automatically handled.
     * @param maxDistance Optional. Maximum distance in miles for nearby results. Requires both `lat` and `lon`.
     * @returns PropertySearchResponse A simplified list of matched properties.
     * @throws ApiError
     */
    public search(
        q: string,
        lat?: number,
        lon?: number,
        state?: string,
        zipCode?: string,
        maxDistance?: number,
    ): CancelablePromise<Array<PropertySearchResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/property/search',
            query: {
                'q': q,
                'lat': lat,
                'lon': lon,
                'state': state,
                'zip_code': zipCode,
                'max_distance': maxDistance,
            },
        });
    }
    /**
     * @param streetAddress
     * @param city
     * @param state
     * @param zipCode
     * @returns any Ok
     * @throws ApiError
     */
    public streetviewImg(
        streetAddress: string,
        city: string,
        state: string,
        zipCode: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/property/streetview',
            query: {
                'street_address': streetAddress,
                'city': city,
                'state': state,
                'zip_code': zipCode,
            },
        });
    }
    /**
     * Lookup parcel owner information for a given address.
     * Returns aggregated owner records plus address lookup context from our parcel provider.
     * @param id
     * @param streetAddress Street address, e.g. "195 Waukegan Road"
     * @param city City name, e.g. "Glenview"
     * @param state Two-letter state code, e.g. "IL"
     * @param zipCode 5-digit ZIP or ZIP+4
     * @param lat
     * @param lon
     * @returns OwnerDataResponse Ok
     * @throws ApiError
     */
    public ownerData(
        id: string,
        streetAddress: string,
        city: string,
        state: string,
        zipCode: string,
        lat: number,
        lon: number,
    ): CancelablePromise<OwnerDataResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/property/owner_data',
            query: {
                'id': id,
                'street_address': streetAddress,
                'city': city,
                'state': state,
                'zip_code': zipCode,
                'lat': lat,
                'lon': lon,
            },
        });
    }
    /**
     * Retrieve all the details of a property given its id.
     *
     * - **ID changes**: it is rare, but can happen, to see the id of a property change. We consider it unlikely for bigger properties since they tend to have a more established "identity" among our data pipelines. For example, if multiples properties are merged into one, or major data changes make us change the id. In this case, you might receive a 404 error. The property search endpoint is free, so always feel free to query it in case that happens.
     * - **Payload usage**: this payload can be used to get the property details, the unit pricing, the expense benchmarks, and the rent comps. And you are free to cache it if you need to.
     * - **Date filtering**: You can optionally filter units, history, availability periods, and concessions by providing `from` and/or `to` date parameters. Only data that overlaps with the specified date range will be included. You can provide either date, both, or neither. This is particularly useful when you only need current or recent data, as it can significantly speed up API calls and reduce response payload size compared to retrieving the full historical dataset.
     *
     * We make a few properties free to access to help with testing. These three properties are listed below but if you would like to use a different property to test with, we are happy to allow that for you.
     *
     * | ID                                   | Building Name        | Address                                       |
     * |--------------------------------------|----------------------|-----------------------------------------------|
     * | e8769b06-1334-5ab6-b9c6-6dfccbf9ea78 | Reserve at Glenview  | 195 Waukegan Road, Glenview, IL 60025         |
     * | 20e4945a-ce78-5f2c-9053-6bdaca39044b | 7229-33 S Yates Blvd | 7229 South Yates Boulevard, Chicago, IL 60649 |
     * | 0e628031-57b8-5394-821d-13779318fef4 | OneEleven            | 111 West Wacker Drive, Chicago, IL 60601      |
     * @param id The property id (UUIDv5 retrieved from the search endpoint).
     * @param from Optional. Filter results to include only data that overlaps with dates on or after this date (ISO 8601 format, e.g., "2024-01-01").
     * @param to Optional. Filter results to include only data that overlaps with dates on or before this date (ISO 8601 format, e.g., "2024-12-31").
     * @returns PropertyDetailsResponse The property details.
     * @throws ApiError
     */
    public getProperty(
        id: UUID,
        from?: string,
        to?: string,
    ): CancelablePromise<PropertyDetailsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/property/{id}',
            path: {
                'id': id,
            },
            query: {
                'from': from,
                'to': to,
            },
        });
    }
    /**
     * @param id
     * @param requestBody
     * @returns any Ok
     * @throws ApiError
     */
    public patchProperty(
        id: UUID,
        requestBody: {
            property: PropertyReportPropertyInsert;
        },
    ): CancelablePromise<{
        id: string;
    }> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/property/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }
    /**
     * Find the best rent comps for a property given its details among close by properties.
     * Score different facets of all candidates and return the best ones. In order to get the best results,
     * we leverage market demographics, detected quality, amenities, similarity of unit mixes, and more.
     *
     * - **Data completion**: feel free to complete the record with extra data only you have access to or if you want to override the data we have. For example, what would be the rent of the property if it was in a better condition? You can increase the quality of the property and see how the comps change.
     * - **Extra Filters**: you can set extra filters. For example, you can set a maximum distance between the subject and the rent comps, a minimum number of units, a maximum number of units, a minimum year built, and a maximum year built.
     * - **Exclusions**: you can exclude properties from the results. For example, you can exclude properties that are not good comps or properties that have already been selected. We will make sure we don't return them in the results.
     * - **Already Selected**: you can also pass the ids of the properties that you want to force into the results.
     *
     * ### How to craft the payload:
     *
     * The endpoint accepts two types of payloads:
     *
     * **Option 1: Detailed Property (Recommended)**
     *
     * Use the result from `/property/{id}` endpoint as the `subject` field:
     *
     * ```typescript
     * {
         * subject: PropertyDetailsResponse, // from /property/{id}
         * excluded_ids?: string[],         // optional: exclude properties
         * selected_ids?: string[]          // optional: force include properties
         * }
         * ```
         *
         * **Option 2: Simple Property**
         *
         * Use a simplified property object with basic information:
         *
         * ```typescript
         * {
             * simple_subject: {
                 * lat: number,                    // required: latitude
                 * lon: number,                    // required: longitude
                 * is_single_family: boolean,      // required: property type
                 * is_apartment: boolean,          // required: property type
                 * is_condo: boolean,              // required: property type
                 * number_units?: number,          // optional: total units
                 * year_built?: number,            // optional: construction year
                 * number_stories?: number,        // optional: building height
                 * avg_quality_prediction?: number, // optional: quality score (0-1)
                 * building_amenities?: string[],   // optional: building features
                 * unit_amenities?: string[],       // optional: unit features
                 * unit_mix?: Array<{              // optional: unit distribution
                 * bed: number, bath: number, count: number, avg_sqft: number
                 * }>
                 * },
                 * excluded_ids?: string[],          // optional: exclude properties
                 * selected_ids?: string[]           // optional: force include properties
                 * }
                 * ```
                 *
                 * **Recommendation**: Use Option 1 (detailed property) for best results. It provides comprehensive property information including market statistics, quality scores, unit details, and automatically detected values for missing fields.
                 *
                 * **When to use simple property**: Use Option 2 when you only have basic property information from your own systems and want to find similar properties based on fundamental characteristics like location, unit count, and amenities.
                 *
                 * ### Computing Similarities:
                 *
                 * If you are interested in simply getting similarities between properties, you can use the `comparables` endpoint.
                 * Pass the ids of the comps you want similarities for into the `selected_ids` field.
                 * And set `topN` to the same number of properties you are passing.
                 * We will return the similarities between the subject and the selected properties.
                 * @param requestBody
                 * @param topN The number of rent comps to return.
                 * @param tier The tier of the property. Either 'valueAdd' or 'current'. When 'valueAdd', the results will assume a better condition of the property.
                 * @param renovations The renovations to apply to the property. A value between 0.0 and 1.0 to add to the property quality. (e.g. 0.1 will transform a 0.4 quality property into a 0.5 quality property).
                 * @param maxDistance The maximum distance in miles between the subject and the rent comps.
                 * @param minDistance The minimum distance in miles between the subject and the rent comps.
                 * @param minNumberUnits The minimum number of units of the rent comps. Does not filter properties with no units.
                 * @param maxNumberUnits The maximum number of units of the rent comps. Does not filter properties with no units.
                 * @param allowNullNumberUnits When a number of units range is specified, whether to allow properties with no units to be included in the results. False by default.
                 * @param minYearBuilt The minimum year built of the rent comps. Does not filter properties with no year built.
                 * @param maxYearBuilt The maximum year built of the rent comps. Does not filter properties with no year built.
                 * @param allowNullYearBuilt When a year built range is specified, whether to allow properties with no year built to be included in the results. False by default.
                 * @param minNumberStories The minimum number of stories of the rent comps. Does not filter properties with no stories.
                 * @param maxNumberStories The maximum number of stories of the rent comps. Does not filter properties with no stories.
                 * @param allowNullNumberStories When a number of stories range is specified, whether to allow properties with no stories to be included in the results. False by default.
                 * @param useEmbedding
                 * @param isAffordable
                 * @param isSenior
                 * @param isStudent
                 * @param maxMilesToUniversity
                 * @param maxMilesToHospital
                 * @returns CompsResponse The list of rent comps with high-level details.
                 * @throws ApiError
                 */
                public comparables(
                    requestBody: CompsPayload,
                    topN?: number,
                    tier?: 'valueAdd' | 'current',
                    renovations?: number,
                    maxDistance?: number,
                    minDistance?: number,
                    minNumberUnits?: number,
                    maxNumberUnits?: number,
                    allowNullNumberUnits?: boolean,
                    minYearBuilt?: number,
                    maxYearBuilt?: number,
                    allowNullYearBuilt?: boolean,
                    minNumberStories?: number,
                    maxNumberStories?: number,
                    allowNullNumberStories?: boolean,
                    useEmbedding?: boolean,
                    isAffordable?: boolean,
                    isSenior?: boolean,
                    isStudent?: boolean,
                    maxMilesToUniversity?: number,
                    maxMilesToHospital?: number,
                ): CancelablePromise<CompsResponse> {
                    return this.httpRequest.request({
                        method: 'POST',
                        url: '/property/comparables',
                        query: {
                            'topN': topN,
                            'tier': tier,
                            'renovations': renovations,
                            'maxDistance': maxDistance,
                            'minDistance': minDistance,
                            'minNumberUnits': minNumberUnits,
                            'maxNumberUnits': maxNumberUnits,
                            'allowNullNumberUnits': allowNullNumberUnits,
                            'minYearBuilt': minYearBuilt,
                            'maxYearBuilt': maxYearBuilt,
                            'allowNullYearBuilt': allowNullYearBuilt,
                            'minNumberStories': minNumberStories,
                            'maxNumberStories': maxNumberStories,
                            'allowNullNumberStories': allowNullNumberStories,
                            'useEmbedding': useEmbedding,
                            'isAffordable': isAffordable,
                            'isSenior': isSenior,
                            'isStudent': isStudent,
                            'maxMilesToUniversity': maxMilesToUniversity,
                            'maxMilesToHospital': maxMilesToHospital,
                        },
                        body: requestBody,
                        mediaType: 'application/json',
                    });
                }
                /**
                 * Insert a new property in the database. For example a new development.
                 * @param requestBody
                 * @returns any The property details.
                 * @throws ApiError
                 */
                public postProperty(
                    requestBody: {
                        property: PropertyReportPropertyInsert;
                    },
                ): CancelablePromise<{
                    id: string;
                }> {
                    return this.httpRequest.request({
                        method: 'POST',
                        url: '/property',
                        body: requestBody,
                        mediaType: 'application/json',
                    });
                }
                /**
                 * Get pricing recommendations for active units among a subject property given its details. We use all property and unit details (characteristics, amenities, location, etc.)
                 * to find similar units in the market that have been rented recently. We then use the rent prices of these units to estimate the unit pricing.
                 * This endpoint is deprecated. Please use the `market_rents` endpoint instead.
                 * @param requestBody
                 * @returns PricingResponse The unit pricing.
                 * @throws ApiError
                 */
                public pricing(
                    requestBody: PricingPayload,
                ): CancelablePromise<Array<PricingResponse>> {
                    return this.httpRequest.request({
                        method: 'POST',
                        url: '/property/pricing',
                        body: requestBody,
                        mediaType: 'application/json',
                    });
                }
                /**
                 * Get market rents for active units among a subject property given its details. We use all property and unit details (characteristics, amenities, location, etc.)
                 * to find similar units in the market that have been rented recently. We then use the rent prices of these units to estimate the unit pricing.
                 *
                 * This is an alias for the pricing endpoint with a more descriptive name.
                 * @param requestBody
                 * @returns PricingResponse The unit pricing.
                 * @throws ApiError
                 */
                public marketRents(
                    requestBody: PricingPayload,
                ): CancelablePromise<Array<PricingResponse>> {
                    return this.httpRequest.request({
                        method: 'POST',
                        url: '/property/market_rents',
                        body: requestBody,
                        mediaType: 'application/json',
                    });
                }
                /**
                 * Get the expense benchmarks for a property given its details.
                 * Our models use all property details (characteristics, #stories, amenities, location, etc.) to compute the estimates.
                 * We recommend to use the Property Details endpoint to get the property details and pass it to this endpoint.
                 *
                 * **Important**: This endpoint returns ML-based predictions only, not actual tax records or verified expense data.
                 * The UI Financial Analysis tab may display actual tax values when available (from licensed data providers), but these actual values cannot be exposed via API due to data licensing restrictions.
                 * All values returned by this endpoint are predictions/estimates based on machine learning models.
                 * @param requestBody
                 * @param customGpr
                 * @returns ExpenseBenchmarksResponse The expense benchmarks (predicted values only).
                 * @throws ApiError
                 */
                public expenseBenchmarks(
                    requestBody: PropertyDetailsResponse,
                    customGpr?: number,
                ): CancelablePromise<ExpenseBenchmarksResponse> {
                    return this.httpRequest.request({
                        method: 'POST',
                        url: '/property/expense_benchmarks',
                        query: {
                            'customGPR': customGpr,
                        },
                        body: requestBody,
                        mediaType: 'application/json',
                    });
                }
                /**
                 * Forecast next N weeks of values for a property given historical data.
                 * @param requestBody
                 * @returns ForecastResponse The forecasted values.
                 * @throws ApiError
                 */
                public forecast(
                    requestBody: ForecastPayload,
                ): CancelablePromise<ForecastResponse> {
                    return this.httpRequest.request({
                        method: 'POST',
                        url: '/property/forecast',
                        body: requestBody,
                        mediaType: 'application/json',
                    });
                }
                /**
                 * Forecast next N weeks of values for a property given historical data.
                 * @param requestBody
                 * @returns RecommendationResponse The forecasted values.
                 * @throws ApiError
                 */
                public revenueManagement(
                    requestBody: RevenueManagementPayload,
                ): CancelablePromise<RecommendationResponse> {
                    return this.httpRequest.request({
                        method: 'POST',
                        url: '/property/revenue_management',
                        body: requestBody,
                        mediaType: 'application/json',
                    });
                }
            }
